import type * as router from '@attain-apps/fe/web/main/lib/router'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * Navigation transition between portal apps.
 */
export type AppTransition = AppTransition.Params
export namespace AppTransition {
  /**
   * Returns whether navigation crosses the portal app boundary. Same-app page
   * changes should use local content loaders instead of the app-switch
   * transition veil.
   */
  export function shouldShow(params: Params): boolean {
    // Feature-owned navigation passes the original router request, while
    // routerLink/direct router navigation already has a resolved target URL.
    const targetUrl =
      'request' in params
        ? _getTargetUrl(params.request, params.routerFeature)
        : params.targetUrl

    return _shouldShowForUrls({
      currentUrl: params.currentUrl,
      targetUrl,
    })
  }

  export type Params = Params.FromRequest | Params.FromUrl
  export namespace Params {
    export interface FromRequest {
      readonly currentUrl: string
      readonly request: router.models.Request
      readonly routerFeature: router.Router
    }

    export interface FromUrl {
      readonly currentUrl: string
      readonly targetUrl: string | undefined
    }
  }

  function _shouldShowForUrls({
    currentUrl,
    targetUrl,
  }: {
    currentUrl: string
    targetUrl: string | undefined
  }): boolean {
    const targetPortalRoute = _getPortalRoute(targetUrl)

    if (targetPortalRoute === undefined) {
      return false
    }

    const currentPortalRoute = _getPortalRoute(currentUrl)

    if (currentPortalRoute === undefined) {
      return true
    }

    // Only cross-app portal navigation gets the app-level transition veil.
    // Same-app page changes should keep their local content loading behavior.
    // Home has no app alias, so app -> home and home -> app both count as
    // boundary changes while home -> home does not.
    return targetPortalRoute.appAlias !== currentPortalRoute.appAlias
  }

  function _getTargetUrl(
    request: router.models.Request,
    routerFeature: router.Router,
  ): string | undefined {
    // Router requests can carry either a prebuilt URL/UrlTree or route
    // commands. Normalize both shapes so the boundary check works uniformly.
    if (request.url) {
      return typeof request.url === 'string'
        ? request.url
        : routerFeature.urlSerializer.serialize(request.url)
    }

    if (!request.path) {
      return undefined
    }

    return routerFeature.urlSerializer.serialize(
      routerFeature.ngRouter.createUrlTree(request.path as string[]),
    )
  }

  function _getPortalRoute(url: string | undefined): PortalRoute | undefined {
    if (!url) {
      return undefined
    }

    const path = _getPathname(url)
    const segments = path.split('/').filter(_isPathSegment)
    const appAlias = segments[3]

    // Portal app routes are shaped as
    // /portal/:tenantAlias/:zoneAlias/:appAlias. Portal home stops at zone.
    if (
      segments[0] !== 'portal' ||
      !TypeChecker.isString(segments[1]) ||
      !TypeChecker.isString(segments[2])
    ) {
      return undefined
    }

    return {
      appAlias: TypeChecker.isString(appAlias) ? appAlias : undefined,
    }
  }

  function _getPathname(url: string): string {
    try {
      // Absolute and root-relative URLs both normalize correctly with the
      // browser URL parser when a base origin is supplied.
      return new URL(url, window.location.origin).pathname
    } catch {
      // Some router commands serialize to app-local strings that URL rejects.
      // Fall back to the path portion so query/hash changes do not affect the
      // app boundary comparison.
      return url.split('?')[0]?.split('#')[0] ?? url
    }
  }

  function _isPathSegment(segment: unknown): segment is string {
    return TypeChecker.isString(segment) && segment.length > 0
  }

  interface PortalRoute {
    readonly appAlias: string | undefined
  }
}
